/**
 * BasketballParallel — per-week SVG court diagram that animates in sync with
 * the student's chess progress. Each week teaches a distinct court concept
 * (full-court press, corner double-team, box-and-one, baseline trap, fast break,
 * 4th-quarter close-out). progress/total advances the animation across each phase.
 */
import React from 'react';

const GOLD = '#C9A961';
const ORANGE = '#FF5E00';
const RED = '#EF4444';
const BLUE = '#3B82F6';
const WHITE = '#F5ECC8';
const COURT_BG = '#0A0A0E';
const LINE = '#374151';

// Half-court diagram base
const Court = ({ children, width = 400, height = 400 }) => (
  <svg viewBox="0 0 400 400" className="w-full h-auto" style={{ maxWidth: 400 }}>
    <rect x="0" y="0" width="400" height="400" fill={COURT_BG} />
    {/* Court outline (half court) */}
    <rect x="10" y="10" width="380" height="380" fill="none" stroke={LINE} strokeWidth="2" />
    {/* Free throw lane (paint) */}
    <rect x="140" y="10" width="120" height="140" fill="none" stroke={LINE} strokeWidth="2" />
    {/* Free throw line arc */}
    <path d="M 140 150 A 60 60 0 0 0 260 150" fill="none" stroke={LINE} strokeWidth="2" />
    {/* Three-point line */}
    <path d="M 40 10 L 40 100 A 160 160 0 0 0 360 100 L 360 10" fill="none" stroke={LINE} strokeWidth="2" />
    {/* Basket */}
    <circle cx="200" cy="40" r="8" fill="none" stroke={ORANGE} strokeWidth="2" />
    {/* Half court line */}
    <line x1="10" y1="390" x2="390" y2="390" stroke={LINE} strokeWidth="2" />
    {children}
  </svg>
);

// Utility for animated player dot with label
const Player = ({ x, y, color, label, active = false }) => (
  <g style={{ transition: 'all 0.6s ease-out' }}>
    {active && <circle cx={x} cy={y} r="16" fill={color} opacity="0.25" />}
    <circle cx={x} cy={y} r="10" fill={color} stroke="#fff" strokeWidth="1.5" />
    <text x={x} y={y + 4} textAnchor="middle" fontSize="10" fontWeight="900" fill="#fff">{label}</text>
  </g>
);

const Arrow = ({ x1, y1, x2, y2, color = GOLD, visible = true }) => (
  <g opacity={visible ? 1 : 0} style={{ transition: 'opacity 0.4s' }}>
    <defs>
      <marker id={`arrow-${x1}-${y1}`} markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto">
        <polygon points="0 0, 10 3, 0 6" fill={color} />
      </marker>
    </defs>
    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="2.5" strokeDasharray="4 3"
          markerEnd={`url(#arrow-${x1}-${y1})`} />
  </g>
);

const Ball = ({ x, y }) => (
  <circle cx={x} cy={y} r="6" fill={ORANGE} stroke="#000" strokeWidth="1.5" style={{ transition: 'all 0.6s ease-out' }} />
);

// -------- Per-week scenes --------

// Week 1 — Full-Court Press vs Scholar's Mate
// Defenders converge on inbounder; escape via disciplined outlet passes
const Week1 = ({ progress, total }) => {
  const t = total > 0 ? progress / total : 0;
  // Ball handler starts baseline, moves up as press is broken
  const bx = 200; const by = 380 - t * 100;
  return (
    <Court>
      {/* Defenders pressing */}
      <Player x={140} y={340} color={RED} label="D1" active={t < 0.4} />
      <Player x={260} y={340} color={RED} label="D2" active={t < 0.4} />
      <Player x={100} y={290} color={RED} label="D3" />
      <Player x={300} y={290} color={RED} label="D4" />
      {/* Ball handler — the "calm king defender" */}
      <Player x={bx} y={by} color={GOLD} label="PG" active={true} />
      {/* Outlet passes */}
      <Arrow x1={bx - 15} y1={by} x2={140} y2={230} color={GOLD} visible={t >= 0.33} />
      <Arrow x1={bx + 15} y1={by} x2={260} y2={230} color={GOLD} visible={t >= 0.66} />
      {/* Outlet receivers */}
      <Player x={140} y={230} color={BLUE} label="F1" active={t >= 0.33} />
      <Player x={260} y={230} color={BLUE} label="F2" active={t >= 0.66} />
      <Ball x={bx} y={by - 12} />
      <text x="200" y="30" textAnchor="middle" fontSize="12" fontWeight="900" fill={GOLD}>PRESS BREAK</text>
    </Court>
  );
};

// Week 2 — Corner Double-Team vs Fried Liver
// Two defenders converge on corner; escape via cross-court pass
const Week2 = ({ progress, total }) => {
  const t = total > 0 ? progress / total : 0;
  return (
    <Court>
      {/* Ball handler trapped in corner */}
      <Player x={40} y={330} color={GOLD} label="BH" active={t < 0.5} />
      {/* Two defenders trapping */}
      <Player x={90} y={330} color={RED} label="D1" active={t < 0.66} />
      <Player x={40} y={280} color={RED} label="D2" active={t < 0.66} />
      {/* Escape pass to top of key */}
      <Arrow x1={60} y1={315} x2={200} y2={200} color={GOLD} visible={t >= 0.33} />
      <Player x={200} y={200} color={BLUE} label="C" active={t >= 0.33} />
      {/* Counter-punch: swing pass to wing for open 3 */}
      <Arrow x1={210} y1={195} x2={340} y2={100} color={ORANGE} visible={t >= 0.66} />
      <Player x={340} y={100} color={BLUE} label="W" active={t >= 0.66} />
      <Ball x={40} y={318} />
      <text x="200" y="30" textAnchor="middle" fontSize="12" fontWeight="900" fill={GOLD}>CORNER TRAP ESCAPE</text>
    </Court>
  );
};

// Week 3 — Box-and-One vs Absolute Pin
// Face-guarder attached to star; screens shift the geometry
const Week3 = ({ progress, total }) => {
  const t = total > 0 ? progress / total : 0;
  return (
    <Court>
      {/* Star shooter face-guarded */}
      <Player x={140} y={220 - t * 40} color={GOLD} label="★" active={true} />
      <Player x={155} y={220 - t * 40} color={RED} label="FG" active={t < 0.66} />
      {/* Zone box defenders */}
      <Player x={200} y={100} color={RED} label="D1" />
      <Player x={200} y={180} color={RED} label="D2" />
      <Player x={80} y={260} color={RED} label="D3" />
      <Player x={320} y={260} color={RED} label="D4" />
      {/* Screener */}
      <Player x={200} y={220 - t * 40} color={BLUE} label="S" active={t >= 0.33} />
      {/* Cut arrow after screen */}
      <Arrow x1={140} y1={200} x2={80} y2={80} color={ORANGE} visible={t >= 0.66} />
      <text x="200" y="30" textAnchor="middle" fontSize="12" fontWeight="900" fill={GOLD}>BREAK THE FACE-GUARD</text>
    </Court>
  );
};

// Week 4 — Baseline 2-on-1 Trap vs Noah's Ark
// Three pawn-defenders form a cage on baseline
const Week4 = ({ progress, total }) => {
  const t = total > 0 ? progress / total : 0;
  return (
    <Court>
      {/* Trapped ball handler in baseline corner */}
      <Player x={60} y={370} color={GOLD} label="BB" active={true} />
      {/* Three defenders building the cage — appear in sequence like a6, b5, c4 */}
      <Player x={110} y={370} color={RED} label="D1" active={t >= 0.25} />
      <Player x={60} y={320} color={RED} label="D2" active={t >= 0.5} />
      <Player x={110} y={320} color={RED} label="D3" active={t >= 0.75} />
      {/* Cage rectangle — appears at 100% */}
      {t >= 1 && (
        <rect x="30" y="290" width="110" height="105" fill="none" stroke={RED} strokeWidth="3" strokeDasharray="6 4">
          <animate attributeName="opacity" values="0.4;1;0.4" dur="1.6s" repeatCount="indefinite" />
        </rect>
      )}
      {/* Chess pawn labels next to each defender to make the parallel explicit */}
      <text x="130" y="373" fontSize="9" fontWeight="900" fill="#C9A961" opacity={t >= 0.25 ? 1 : 0}>a6</text>
      <text x="80" y="313" fontSize="9" fontWeight="900" fill="#C9A961" opacity={t >= 0.5 ? 1 : 0}>b5</text>
      <text x="130" y="313" fontSize="9" fontWeight="900" fill="#C9A961" opacity={t >= 0.75 ? 1 : 0}>c4</text>
      {/* Court + baseline text */}
      <text x="200" y="30" textAnchor="middle" fontSize="12" fontWeight="900" fill={GOLD}>NOAH'S ARK · BASELINE CAGE</text>
      <text x="60" y="395" textAnchor="middle" fontSize="8" fill={LINE}>OUT OF BOUNDS</text>
    </Court>
  );
};

// Week 5 — Fast Break / Discovered Attack
// 3-on-2 fast break with numbers advantage
const Week5 = ({ progress, total }) => {
  const t = total > 0 ? progress / total : 0;
  const advance = 260 - t * 200;
  return (
    <Court>
      {/* Retreating defenders */}
      <Player x={160} y={120} color={RED} label="D1" active={t >= 0.5} />
      <Player x={240} y={120} color={RED} label="D2" active={t >= 0.5} />
      {/* Fast break trio */}
      <Player x={200} y={advance} color={GOLD} label="PG" active={true} />
      <Player x={120} y={advance + 30} color={BLUE} label="W1" active={true} />
      <Player x={280} y={advance + 30} color={BLUE} label="W2" active={true} />
      {/* Discovered attack: PG draws defender, dishes to wing */}
      <Arrow x1={195} y1={advance - 5} x2={280} y2={advance + 20} color={ORANGE} visible={t >= 0.5} />
      <Arrow x1={200} y1={advance} x2={200} y2={60} color={GOLD} visible={t >= 0.75} />
      <Ball x={200} y={advance - 12} />
      <text x="200" y="30" textAnchor="middle" fontSize="12" fontWeight="900" fill={GOLD}>3-ON-2 FAST BREAK</text>
    </Court>
  );
};

// Week 6 — 4th Quarter Close-Out (Lucena bridge)
// Ball movement + free throw line = closing pattern
const Week6 = ({ progress, total }) => {
  const t = total > 0 ? progress / total : 0;
  return (
    <Court>
      {/* Ball at free throw line — the "closer" */}
      <Player x={200} y={150} color={GOLD} label="C" active={true} />
      {/* Defender committing */}
      <Player x={200} y={195 + t * 30} color={RED} label="D" active={t < 0.5} />
      {/* Corner shooter for kickout */}
      <Player x={340} y={340} color={BLUE} label="3" active={t >= 0.5} />
      {/* Pass to corner (Lucena "bridge" analog) */}
      <Arrow x1={220} y1={155} x2={330} y2={330} color={ORANGE} visible={t >= 0.5} />
      {/* Shot arc for the free throw / corner three */}
      <path d={t >= 0.75 ? "M 340 340 Q 300 150 200 50" : ""} stroke={ORANGE} strokeWidth="2" strokeDasharray="4 3" fill="none">
        <animate attributeName="opacity" values="0.3;1;0.3" dur="1.4s" repeatCount="indefinite" />
      </path>
      <Ball x={200} y={140} />
      <text x="200" y="30" textAnchor="middle" fontSize="12" fontWeight="900" fill={GOLD}>4Q CLOSE-OUT · LUCENA BRIDGE</text>
    </Court>
  );
};

const WEEK_SCENES = { 1: Week1, 2: Week2, 3: Week3, 4: Week4, 5: Week5, 6: Week6 };

const BasketballParallel = ({ week, progress, total, kind }) => {
  const Scene = WEEK_SCENES[week] || Week1;
  const stateLabel =
    progress === 0 ? 'Ready — play the target line on the board.' :
    progress === total && total > 0 ? (kind === 'escape' ? 'Trap escaped — court parallel complete.' : 'Trap sprung — the opponent has nowhere to go.') :
    'Chess sequence in motion — watch the court parallel activate.';

  return (
    <div>
      <Scene progress={progress} total={total} />
      <div className="mt-2 text-[10px] font-black uppercase tracking-widest text-zinc-500">
        {stateLabel}
      </div>
    </div>
  );
};

export default BasketballParallel;
